import { currentLang } from '@/components/site/SiteLang';
import { BACKEND_URL } from '@/config/api';

// Solo se envía el final de la conversación y sin mensajes vacíos ni gigantes:
// antes, tras una respuesta larga, todos los mensajes siguientes fallaban.
function _trimHistory(messages) {
  return (messages || [])
    .filter((m) => m && (m.role === 'user' || m.role === 'assistant') && (m.content || '').trim())
    .slice(-14)
    .map((m) => ({ role: m.role, content: String(m.content).slice(0, 7000) }));
}

// Mensajes de error locales (los del servidor llegan ya traducidos)
const ERR = {
  es: {
    network: 'Error de conexión. Comprueba tu conexión a internet.',
    rate: 'Has enviado muchos mensajes seguidos. Espera un momento y vuelve a intentarlo.',
    session: 'Tu sesión ha caducado. Vuelve a entrar en el panel.',
    generic: 'Ha ocurrido un error. Inténtalo de nuevo.',
    read: 'Error leyendo la respuesta del servidor.',
  },
  en: {
    network: 'Connection error. Please check your internet connection.',
    rate: 'You’ve sent a lot of messages in a row. Please wait a moment and try again.',
    session: 'Your session has expired. Please sign in to the dashboard again.',
    generic: 'Something went wrong. Please try again.',
    read: 'Error reading the server’s response.',
  },
};
const err = (k) => (ERR[currentLang()] || ERR.es)[k];

async function _streamChat(url, messages, headers, { onChunk, onDone, onError }) {
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Lang': currentLang(), ...headers },
      body: JSON.stringify({ messages: _trimHistory(messages) }),
    });
  } catch (err) {
    onError(err('network'));
    return;
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (res.status === 429) onError(body.detail || err('rate'));
    else if (res.status === 401) onError(err('session'));
    else onError(typeof body.detail === 'string' ? body.detail : err('generic'));
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop(); // keep potentially incomplete last line

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const payload = line.slice(6).trim();
        if (payload === '[DONE]') {
          onDone();
          return;
        }
        try {
          const parsed = JSON.parse(payload);
          if (parsed.text) onChunk(parsed.text);
          if (parsed.error) onError(parsed.error);
        } catch {
          // skip malformed SSE line
        }
      }
    }
    onDone();
  } catch (err) {
    onError(err('read'));
  }
}

export function streamPublicChat(messages, callbacks) {
  return _streamChat(
    `${BACKEND_URL}/api/chat/public`,
    messages,
    {},
    callbacks,
  );
}

export function streamPrivateChat(messages, callbacks) {
  const token = sessionStorage.getItem('dashboard_token');
  return _streamChat(
    `${BACKEND_URL}/api/chat/private`,
    messages,
    token ? { Authorization: `Bearer ${token}` } : {},
    callbacks,
  );
}

// Freedy SDR chat for a specific funnel lead. Same SSE contract as the others.
export function streamFunnelChat(leadId, messages, callbacks) {
  return _streamChat(
    `${BACKEND_URL}/api/funnel/lead/${leadId}/chat`,
    messages,
    {},
    callbacks,
  );
}
